import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { ApiError } from "../lib/api";
import { Button } from "../components/Button";
import { BrandMark } from "../components/BrandMark";
import { Footer } from "../components/Footer";

export function ChangePasswordPage() {
  const { changePassword } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (next !== confirm) {
      setError("New passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await changePassword(current, next);
      toast.success("Password changed.");
      navigate("/app");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not change password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-full flex-col">
      <div className="flex flex-1 items-center justify-center p-4">
        <div className="w-full max-w-[400px] animate-rise-in">
          <div className="mb-7 flex flex-col items-center gap-3 text-center">
            <BrandMark size={40} />
            <div>
              <h1 className="text-base font-semibold tracking-tight text-zinc-900">Set a new password</h1>
              <p className="mt-1 text-[13px] leading-relaxed text-zinc-500">
                You are signed in with the default credentials. Choose a password to continue.
              </p>
            </div>
          </div>

          <form onSubmit={onSubmit} className="glass glass-strong space-y-4 rounded-2xl p-6 shadow-glass-lg">
            <label className="block">
              <span className="mb-1.5 block text-[13px] font-medium text-zinc-700">Current password</span>
              <input
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoComplete="current-password"
                className="field"
                required
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-[13px] font-medium text-zinc-700">New password</span>
              <input
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password"
                className="field"
                required
                minLength={10}
              />
              <span className="mt-1.5 block text-2xs text-zinc-500">
                At least 10 characters, including letters and numbers.
              </span>
            </label>
            <label className="block">
              <span className="mb-1.5 block text-[13px] font-medium text-zinc-700">Confirm new password</span>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                className="field"
                required
              />
            </label>

            {error && (
              <p className="rounded-lg border border-red-200 bg-red-50/80 px-3 py-2 text-[13px] text-red-700">
                {error}
              </p>
            )}

            <Button type="submit" variant="primary" size="lg" loading={busy} className="w-full">
              Change password and continue
            </Button>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
}
